import { withListOverflow } from "../../v2/components/ListOverflow.jsx";
export const WeeklyPortfolioReview = withListOverflow(WeeklyReviewListOwner);
// src/features/reports/WeeklyPortfolioReview.jsx
//
// WPR Slice 3 + WPR-SNAPSHOT — Weekly Portfolio Review with week-navigation,
// freeze, and true point-in-time snapshot rendering.
//
// WPR-SNAPSHOT architectural decision:
//   When a week is frozen, this component fetches the snapshot (GET /api/wpr/snapshot)
//   assembled at freeze time, then derives ALL display state from it:
//     rows, updatesMap, decisions — computed directly from snapshot.payload
//     readOuts (including per-project tracks/weeklyUpdate) — from snapshot.payload.readOuts
//   Frozen reads NEVER re-query projects/tracks/milestones/decisions tables.
//   The EditableUpdate cells become read-only in frozen mode (canEdit = false).
//
// Live weeks use the original query path: projects, locations, milestones,
// weekly-updates, and decisions are all fetched live from the API.
//
// PDF export (handleExportPdf) requires no special-casing: it always uses the
// component-derived `rows` and `updatesMap`, which already reflect snapshot
// data when frozen. Frozen PDF = frozen snapshot. Live PDF = live data.
//
// Calendar policy owns freezing. No role receives manual unfreeze/refreeze
// controls; only the current UTC week allows otherwise-authorized edits.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { T } from "../../app/tokens.js";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import { getProjects, getAllNextMilestones, getAllOpenDecisions } from "../../data/projectsClient.js";
import { currentMondayUtc } from "../../lib/weeklyCalendar.js";
import { getLocations } from "../../data/locationsClient.js";
import {
  getWprUpdates,
  getWprWeeks,
  upsertWprUpdate,
  getWprSnapshot,
} from "../../data/wprClient.js";
import { log } from "../../utils/logger.js";
import { buildWprRows, buildWprReadOutProps, fmtMilestoneDate } from "./wprFilters.js";
import { WprProjectReadOut } from "./WprProjectReadOut.jsx";
import { PaneHeading } from "../../components/PaneHeading.jsx";
import { ReportShell } from "./ReportShell.jsx";
import { useFeatureFlagContext } from "../../contexts/FeatureFlagContext.jsx";
import { getReportNavEligibility } from "./reportNavEligibility.js";
import { escHtml, openPrintWindow } from "./wprPrint.js";

// ─── Constants ────────────────────────────────────────────────────────────────

const WEEK_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const NAV_BTN_BASE = {
  background:   "none",
  border:       `1px solid ${T.border}`,
  borderRadius: 4,
  color:        T.mid,
  cursor:       "pointer",
  fontSize: T.fsBase,
  fontWeight: T.fwBold,
  lineHeight:   1,
  padding:      "2px 8px",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Return today's Monday as a YYYY-MM-DD string using local calendar dates
 * to avoid UTC-shift off-by-one (see memory: wpr-date-serialisation.md).
 */
function currentMondayISO() {
  return currentMondayUtc();
}

/**
 * Format a YYYY-MM-DD week date for display.
 * Uses local-midnight construction to prevent UTC-shift (memory: wpr-date-serialisation.md).
 */
function weekDateLabel(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
}

function formatUpdatedAt(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
}

export function filterWprRowsByEligibility(rows, {
  projectsEnabled = true,
  officesEnabled = true,
  marketsEnabled = true,
  locations = [],
} = {}) {
  const locationById = new Map((Array.isArray(locations) ? locations : []).map((location) => [location.id, location]));
  return (Array.isArray(rows) ? rows : [])
    .filter((row) => row.entityType === "project" ? projectsEnabled : officesEnabled)
    .map((row) => {
      if (marketsEnabled || row.entityType !== "location") return row;
      const alias = locationById.get(row.entityId)?.locationAlias;
      const stage = alias ? String(row.subtext || "").split(" · ").at(-1) : "";
      return { ...row, name: alias || "Site", subtext: stage };
    });
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function RagDot({ color }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display:      "inline-block",
        width:        9,
        height:       9,
        borderRadius: "50%",
        background:   color,
        flexShrink:   0,
      }}
    />
  );
}

function TypePill({ entityType }) {
  const isProject = entityType === "project";
  return (
    <span style={{
      display:    "inline-flex",
      alignItems: "center",
      fontSize:   T.fsXs,
      fontWeight: T.fwSemibold,
      padding:    "2px 10px",
      borderRadius: 6,
      background:  isProject ? T.blueSoft : T.utilGreen,
      color:       isProject ? T.blue     : T.green,
      whiteSpace:  "nowrap",
    }}>
      {isProject ? "Project" : "Site"}
    </span>
  );
}

// ─── EditableUpdate ───────────────────────────────────────────────────────────

function EditableUpdate({ value, canEdit, onSave, updatedBy, updatedAt }) {
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState(value ?? "");
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState(null);

  useEffect(() => {
    if (!editing) setDraft(value ?? "");
  }, [value, editing]);

  function startEdit() {
    setDraft(value ?? "");
    setErr(null);
    setEditing(true);
  }

  function cancelEdit() {
    setDraft(value ?? "");
    setErr(null);
    setEditing(false);
  }

  async function handleSave() {
    setSaving(true);
    setErr(null);
    try {
      await onSave(draft.trim());
      setEditing(false);
    } catch {
      setErr("Save failed - please try again.");
    } finally {
      setSaving(false);
    }
  }

  if (!editing) {
    const metaLine = value && updatedBy
      ? `Last edited by ${updatedBy}${updatedAt ? ` on ${formatUpdatedAt(updatedAt)}` : ""}`
      : null;

    return (
      <span
        style={{
          display:  "block",
          cursor:   canEdit ? "pointer" : "default",
          minHeight: 18,
        }}
        title={canEdit ? "Click to edit" : undefined}
        onClick={canEdit ? startEdit : undefined}
      >
        <span style={{
          display:    "block",
          color:      value ? T.dark : T.mid,
          fontStyle:  value ? "normal" : "italic",
          lineHeight: 1.45,
        }}>
          {value || (canEdit ? "Add update…" : "—")}
        </span>
        {metaLine && (
          <span style={{
            display:    "block",
            fontSize: T.fsXxs,
            color:      T.mid,
            marginTop:  3,
            lineHeight: 1.4,
          }}>
            {metaLine}
          </span>
        )}
      </span>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <textarea
        autoFocus
        rows={2}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        style={{
          width:       "100%",
          fontSize: T.fsSm,
          padding:     "4px 6px",
          borderRadius: 4,
          border:      `1px solid ${T.border}`,
          resize:      "vertical",
          fontFamily:  "inherit",
          boxSizing:   "border-box",
          lineHeight:  1.45,
        }}
      />
      {err && (
        <span style={{ fontSize: T.fsXs, color: T.red }}>{err}</span>
      )}
      <div style={{ display: "flex", gap: 6 }}>
        <button
          type="button"
          disabled={saving}
          onClick={handleSave}
          style={{
            fontSize: T.fsXs,
            padding:      "2px 10px",
            borderRadius: 4,
            background:   T.blue,
            color:        T.white,
            border:       "none",
            cursor:       "pointer",
            fontWeight: T.fwSemibold,
            opacity:      saving ? 0.7 : 1,
          }}
        >
          {saving ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          onClick={cancelEdit}
          style={{
            fontSize: T.fsXs,
            padding:      "2px 10px",
            borderRadius: 4,
            background:   "none",
            color:        T.mid,
            border:       `1px solid ${T.border}`,
            cursor:       "pointer",
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── WeekPicker ───────────────────────────────────────────────────────────────

/**
 * Dropdown week selector with prev/next arrow convenience buttons.
 * `weeks` is sorted newest-first, so:
 *   - "older" = higher index (idx + 1)
 *   - "newer" = lower index  (idx - 1)
 *
 * If `selectedWeek` is not yet in the `weeks` list (e.g. loaded from a URL
 * param before base data arrives, or the current week has no updates yet),
 * a synthetic entry is inserted so the select renders correctly.
 */
function WeekPicker({ weeks, selectedWeek, onSelect, disabled }) {
  // Include selectedWeek in the list even if it has no data yet.
  const allWeeks = weeks.some((w) => w.weekDate === selectedWeek)
    ? weeks
    : [{ weekDate: selectedWeek, frozen: false, frozenAt: null, frozenBy: null }, ...weeks]
        .sort((a, b) => b.weekDate.localeCompare(a.weekDate));

  const idx      = allWeeks.findIndex((w) => w.weekDate === selectedWeek);
  const isFrozen = allWeeks[idx]?.frozen ?? false;

  // Only disable prev/next if we'd go out-of-bounds.
  const canGoPrev = idx < allWeeks.length - 1;
  const canGoNext = idx > 0;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <button
        type="button"
        aria-label="Previous week"
        disabled={!canGoPrev || disabled}
        onClick={() => canGoPrev && onSelect(allWeeks[idx + 1].weekDate)}
        style={{
          ...NAV_BTN_BASE,
          opacity: (!canGoPrev || disabled) ? 0.35 : 1,
          cursor:  (!canGoPrev || disabled) ? "default" : "pointer",
        }}
      >
        ←
      </button>

      {/* Dropdown: lists all known weeks so users can jump directly */}
      <select
        value={selectedWeek}
        disabled={disabled}
        onChange={(e) => onSelect(e.target.value)}
        aria-label="Select week"
        style={{
          fontSize: T.fsSm,
          fontWeight: T.fwSemibold,
          color:        T.mid,
          background:   T.lightGray,
          border:       `1px solid ${T.border}`,
          borderRadius: 6,
          padding:      "3px 8px",
          cursor:       disabled ? "default" : "pointer",
          appearance:   "auto",
        }}
      >
        {allWeeks.map((w) => (
          <option key={w.weekDate} value={w.weekDate}>
            {"Week of " + weekDateLabel(w.weekDate) + (w.frozen ? " (Archived)" : "")}
          </option>
        ))}
      </select>

      {isFrozen && (
        <span style={{
          fontSize: T.fsXxs,
          fontWeight: T.fwBold,
          color:        T.mid,
          background:   T.border,
          borderRadius: 4,
          padding:      "1px 5px",
          letterSpacing: 0.3,
          whiteSpace:   "nowrap",
        }}>
          Archived
        </span>
      )}

      <button
        type="button"
        aria-label="Next week"
        disabled={!canGoNext || disabled}
        onClick={() => canGoNext && onSelect(allWeeks[idx - 1].weekDate)}
        style={{
          ...NAV_BTN_BASE,
          opacity: (!canGoNext || disabled) ? 0.35 : 1,
          cursor:  (!canGoNext || disabled) ? "default" : "pointer",
        }}
      >
        →
      </button>
    </div>
  );
}

/**
 * Build a self-contained HTML document for the WPR print / PDF view.
 * Opens in a new window; the page auto-calls window.print() on load.
 *
 * Exported as a pure function so tests can assert on the generated markup
 * without opening a real browser window (principle B.3 — pure helpers for tests).
 *
 * WPR-SNAPSHOT: no separate frozen path needed. handleExportPdf always passes
 * the component-derived `rows` and `updatesMap`, which already reflect snapshot
 * data when frozen. Frozen PDF = frozen snapshot. Live PDF = live data.
 *
 * @param {Array}  rows        - Roll-up rows from buildWprRows.
 * @param {Map}    updatesMap  - entity_id → update_text.
 * @param {string} weekLabel   - Formatted "May 26, 2026" week-of label.
 * @returns {string} Full HTML document string.
 */
export function buildWprPrintHtml(rows, updatesMap, weekLabel) {
  const projectCount = rows.filter((r) => r.entityType === "project").length;
  const siteCount    = rows.filter((r) => r.entityType === "location").length;

  function ragDot(color) {
    // row.ragColor is already a hex string pulled from T tokens (T.red / T.amber / T.green);
    // inline background prints when the user enables "Print background graphics".
    return `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${escHtml(color)};flex-shrink:0;"></span>`;
  }

  function typePill(entityType) {
    const isProject = entityType === "project";
    // blueSoft / utilGreen pair for Project / Site — matches the live-UI TypePill.
    const bg    = isProject ? T.blueSoft  : T.utilGreen;
    const color = isProject ? T.blue      : T.green;
    return `<span style="font-size:10px;font-weight:700;padding:2px 8px;border-radius:20px;background:${bg};color:${color};letter-spacing:0.3px;white-space:nowrap;">${isProject ? "Project" : "Site"}</span>`;
  }

  const rowsHtml = rows.length === 0
    ? `<tr><td colspan="6" style="padding:24px 16px;text-align:center;color:${T.mid};font-size:13px;">No active projects or flagged sites this week.</td></tr>`
    : rows.map((row) => {
        const updateText = updatesMap.get(row.entityId) ?? "";
        return `
          <tr style="border-bottom:1px solid ${T.border};">
            <td style="padding:10px 12px;text-align:center;vertical-align:middle;">${ragDot(row.ragColor)}</td>
            <td style="padding:10px 12px;vertical-align:top;">${typePill(row.entityType)}</td>
            <td style="padding:10px 12px;vertical-align:top;">
              <div style="font-weight:600;font-size:13px;color:${T.dark};line-height:1.3;">${escHtml(row.name)}</div>
              ${row.subtext ? `<div style="font-size:11px;color:${T.mid};margin-top:2px;">${escHtml(row.subtext)}</div>` : ""}
            </td>
            <td style="padding:10px 12px;vertical-align:top;font-size:12px;color:${updateText ? T.dark : T.mid};font-style:${updateText ? "normal" : "italic"};">${escHtml(updateText) || "\u2014"}</td>
            <td style="padding:10px 12px;vertical-align:top;font-size:12px;color:${T.mid};">${escHtml(row.milestone)}</td>
            <td style="padding:10px 12px;vertical-align:top;font-size:12px;color:${T.mid};">${escHtml(row.owner)}</td>
          </tr>`;
      }).join("");

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>WPR \u2014 Week of ${weekLabel}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; color: ${T.dark}; background: ${T.card}; padding: 32px 40px; }
    h1 { font-size: 20px; font-weight: 800; color: ${T.dark}; letter-spacing: -0.3px; margin-bottom: 4px; }
    .week-badge { display: inline-block; font-size: 12px; font-weight: 600; color: ${T.mid}; background: ${T.lightGray}; border: 1px solid ${T.border}; border-radius: 6px; padding: 2px 10px; margin-left: 10px; vertical-align: middle; }
    .subtitle { font-size: 13px; color: ${T.mid}; margin: 6px 0 20px; }
    table { width: 100%; border-collapse: collapse; table-layout: fixed; }
    thead tr { border-bottom: 1px solid ${T.border}; background: ${T.lightGray}; }
    th { padding: 9px 12px; text-align: left; font-size: 11px; font-weight: 700; color: ${T.mid}; letter-spacing: 0.4px; text-transform: uppercase; white-space: nowrap; }
    col.c-rag   { width: 36px; }
    col.c-type  { width: 76px; }
    col.c-name  { width: 22%; }
    col.c-ms    { width: 190px; }
    col.c-owner { width: 140px; }
    @media print {
      body { padding: 16px 24px; }
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  </style>
</head>
<body>
  <h1>Weekly portfolio review<span class="week-badge">Week of ${weekLabel}</span></h1>
  <p class="subtitle">${rows.length} items \u2014 ${projectCount} active ${projectCount === 1 ? "project" : "projects"}, ${siteCount} flagged ${siteCount === 1 ? "site" : "sites"}</p>
  <table>
    <colgroup>
      <col class="c-rag" />
      <col class="c-type" />
      <col class="c-name" />
      <col />
      <col class="c-ms" />
      <col class="c-owner" />
    </colgroup>
    <thead>
      <tr>
        <th></th>
        <th>Type</th>
        <th>Project / Site</th>
        <th>Update</th>
        <th>Next Milestone</th>
        <th>Owner</th>
      </tr>
    </thead>
    <tbody>
      ${rowsHtml}
    </tbody>
  </table>
  <script>window.onload = function () { window.print(); };<\/script>
</body>
</html>`;
}

// ─── DecisionsBlock ───────────────────────────────────────────────────────────

/**
 * Read-only "Decisions & Actions" section at the bottom of the WPR report.
 * Lists all open decisions grouped by project.
 * Resolving / editing decisions stays on the Project detail page.
 */
function DecisionsBlock({ decisions, decisionsError }) {
  if (decisionsError) {
    return (
      <div style={{
        marginTop:    24,
        padding:      "14px 18px",
        background:   T.card,
        border:       `1px solid ${T.border}`,
        borderRadius: 8,
      }}>
        <PaneHeading style={{ marginBottom: 8 }}>Decisions &amp; actions</PaneHeading>
        <p style={{ margin: 0, fontSize: T.fsSm, color: T.mid, fontStyle: "italic" }}>
          Decisions data unavailable - view open decisions on each Project page.
        </p>
      </div>
    );
  }

  if (!decisions || decisions.length === 0) {
    return (
      <div style={{
        marginTop:    24,
        padding:      "14px 18px",
        background:   T.card,
        border:       `1px solid ${T.border}`,
        borderRadius: 8,
      }}>
        <PaneHeading style={{ marginBottom: 8 }}>Decisions &amp; actions</PaneHeading>
        <p style={{ margin: 0, fontSize: T.fsSm, color: T.mid, fontStyle: "italic" }}>
          No open decisions this week.
        </p>
      </div>
    );
  }

  // Group decisions by projectId, preserving the sorted order they arrived in.
  const groups = [];
  const seenProject = new Map();
  for (const d of decisions) {
    if (!seenProject.has(d.projectId)) {
      const g = { projectId: d.projectId, projectName: d.projectName, items: [] };
      groups.push(g);
      seenProject.set(d.projectId, g);
    }
    seenProject.get(d.projectId).items.push(d);
  }

  return (
    <div style={{
      marginTop:    24,
      padding:      "14px 18px 18px",
      background:   T.card,
      border:       `1px solid ${T.border}`,
      borderRadius: 8,
    }}>
      <PaneHeading style={{ marginBottom: 12 }}>
        Decisions &amp; actions{" "}
        <span style={{
          marginLeft:   8,
          fontSize: T.fsXs,
          fontWeight: T.fwSemibold,
          background:   T.utilAmber,
          color:        T.amberDark,
          borderRadius: 6,
          padding:      "2px 10px",
        }}>
          {decisions.length} open
        </span>
      </PaneHeading>

      {groups.map((g, gi) => (
        <div key={g.projectId} style={{ marginTop: gi === 0 ? 0 : 16 }}>
          <div style={{
            fontSize: T.fsXs,
            fontWeight: T.fwBold,
            color:        T.mid,
            textTransform: "uppercase",
            letterSpacing: 0.4,
            marginBottom:  6,
          }}>
            {g.projectName}
          </div>
          {g.items.map((d) => {
            const dueFmt = fmtMilestoneDate(d.neededByDate);
            return (
              <div key={d.id} style={{
                display:      "flex",
                alignItems:   "baseline",
                gap:          10,
                padding:      "6px 0",
                borderBottom: `1px solid ${T.border}`,
              }}>
                <span style={{
                  width:        6,
                  height:       6,
                  borderRadius: "50%",
                  background:   T.amber,
                  flexShrink:   0,
                  marginTop:    3,
                  display:      "inline-block",
                }} />
                <span style={{ fontSize: T.fsSm, fontWeight: T.fwSemibold, color: T.dark, flex: 1 }}>
                  {d.title}
                </span>
                {d.owner && (
                  <span style={{ fontSize: T.fsXs, color: T.mid, whiteSpace: "nowrap" }}>
                    {d.owner}
                  </span>
                )}
                {dueFmt && (
                  <span style={{ fontSize: T.fsXs, color: T.mid, whiteSpace: "nowrap" }}>
                    Due {dueFmt}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      ))}

      <p style={{ margin: "10px 0 0", fontSize: T.fsXs, color: T.mid, fontStyle: "italic" }}>
        Resolving or editing decisions happens on the Project page.
      </p>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const TH_STYLE = {
  padding:       "10px 14px",
  textAlign:     "left",
  fontSize:      T.fsXxs,
  fontWeight:    T.fwBold,
  color:         "var(--v2-mid)",
  letterSpacing: ".07em",
  textTransform: "uppercase",
  borderBottom:  "1px solid var(--v2-line)",
  whiteSpace:    "nowrap",
};

const TD_STYLE = {
  padding:       "10px 12px",
  verticalAlign: "top",
};

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * @param {{
 *   onBack: () => void,
 *   currentRole: string,
 * }} props
 */
function WeeklyReviewListOwner({ onBack, currentRole, prevReport = null, nextReport = null, onNavigateReport }) {
  const navigate = useNavigate();
  const featureContext = useFeatureFlagContext();
  const { ready: featureFlagsReady, projects: projectsEnabled, offices: officesEnabled, markets: marketsEnabled } = useMemo(
    () => getReportNavEligibility(featureContext),
    [featureContext],
  );
  // ── Live-data state ───────────────────────────────────────────────────────
  const [projects,       setProjects]       = useState([]);
  const [locations,      setLocations]      = useState([]);
  const [milestones,     setMilestones]     = useState([]);
  const [updates,        setUpdates]        = useState([]);
  const [weeks,          setWeeks]          = useState([]);
  // Initialise from ?week=YYYY-MM-DD URL param so frozen weeks are shareable by permalink.
  const [selectedWeek,   setSelectedWeek]   = useState(() => {
    const param = new URLSearchParams(window.location.search).get("week");
    if (param && WEEK_DATE_RE.test(param) && !isNaN(Date.parse(param))) return param;
    return currentMondayISO();
  });
  const [decisions,      setDecisions]      = useState([]);

  const [baseLoading,   setBaseLoading]   = useState(true);
  const [updatesLoading, setUpdatesLoading] = useState(false);

  const [error,          setError]          = useState(null);
  const [decisionsError, setDecisionsError] = useState(null);

  // ── Snapshot state (WPR-SNAPSHOT) ─────────────────────────────────────────
  const [snapshotPayload,  setSnapshotPayload]  = useState(null);
  const [snapshotLoading,  setSnapshotLoading]  = useState(false);
  const [snapshotError,    setSnapshotError]    = useState(null);

  const isEditorOrAdmin = currentRole === "editor" || currentRole === "admin";

  // Derive frozen status for the currently viewed week.
  const selectedWeekInfo = useMemo(
    () => weeks.find((w) => w.weekDate === selectedWeek) ?? { weekDate: selectedWeek, frozen: false },
    [weeks, selectedWeek],
  );
  const isFrozen = selectedWeek < currentMondayUtc() && selectedWeekInfo.frozen;
  const canEdit  = isEditorOrAdmin && selectedWeek === currentMondayUtc();

  // ── Effect: keep ?week= URL param in sync so the current view is shareable ──
  // Uses replace:true so the browser back-button still works.

  useEffect(() => {
    navigate({ search: `?week=${encodeURIComponent(selectedWeek)}` }, { replace: true });
  }, [selectedWeek, navigate]);

  // ── Effect 1: load week-independent data on mount ─────────────────────────

  useEffect(() => {
    let cancelled = false;
    if (!featureFlagsReady || (!projectsEnabled && !officesEnabled)) {
      setProjects([]);
      setLocations([]);
      setMilestones([]);
      setWeeks([]);
      setBaseLoading(false);
      return () => { cancelled = true; };
    }
    setBaseLoading(true);

    Promise.all([
      projectsEnabled ? getProjects() : Promise.resolve([]),
      officesEnabled ? getLocations() : Promise.resolve([]),
      projectsEnabled ? getAllNextMilestones() : Promise.resolve([]),
      getWprWeeks(),
    ])
      .then(([projs, locs, ms, wks]) => {
        if (cancelled) return;
        setProjects(Array.isArray(projs)  ? projs : []);
        setLocations(Array.isArray(locs)  ? locs  : []);
        setMilestones(Array.isArray(ms)   ? ms    : []);
        setWeeks(Array.isArray(wks)       ? wks   : []);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        log.error("WeeklyPortfolioReview: base load failed", { err: String(err) });
        setError(err);
      })
      .finally(() => { if (!cancelled) setBaseLoading(false); });

    return () => { cancelled = true; };
  }, [featureFlagsReady, projectsEnabled, officesEnabled]);

  // Fetch all open decisions for the Decisions & Actions block.
  // Separate effect so a decisions 4xx / network error never breaks the main report.
  // For frozen weeks, decisions are sourced from the snapshot instead (see Effect 3).
  useEffect(() => {
    if (isFrozen || !projectsEnabled) return; // frozen decisions come from snapshot
    let cancelled = false;
    getAllOpenDecisions()
      .then((decs) => {
        if (cancelled) return;
        setDecisions(Array.isArray(decs) ? decs : []);
        setDecisionsError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        log.warn("WeeklyPortfolioReview: decisions load failed", { err: String(err) });
        setDecisionsError(err);
      });
    return () => { cancelled = true; };
  }, [isFrozen, projectsEnabled]);

  // ── Effect 2: reload updates whenever the selected week changes ───────────
  // Skipped for frozen weeks — updates come from the snapshot (Effect 3).

  useEffect(() => {
    if (isFrozen || !featureFlagsReady || (!projectsEnabled && !officesEnabled)) return;
    let cancelled = false;
    setUpdatesLoading(true);

    getWprUpdates(selectedWeek)
      .then((upds) => {
        if (cancelled) return;
        setUpdates(Array.isArray(upds) ? upds : []);
      })
      .catch((err) => {
        if (cancelled) return;
        log.error("WeeklyPortfolioReview: updates load failed", { err: String(err), weekDate: selectedWeek });
        setError(err);
      })
      .finally(() => { if (!cancelled) setUpdatesLoading(false); });

    return () => { cancelled = true; };
  }, [selectedWeek, isFrozen, featureFlagsReady, projectsEnabled, officesEnabled]);

  // ── Effect 3 (WPR-SNAPSHOT): fetch snapshot when week is frozen ───────────
  //
  // Clear snapshotPayload when switching to a live week so that stale data
  // from a previously viewed frozen week is never shown.

  useEffect(() => {
    if (!isFrozen || !featureFlagsReady || (!projectsEnabled && !officesEnabled)) {
      setSnapshotPayload(null);
      setSnapshotError(null);
      return;
    }

    let cancelled = false;
    setSnapshotLoading(true);
    setSnapshotError(null);

    getWprSnapshot(selectedWeek)
      .then((data) => {
        if (cancelled) return;
        setSnapshotPayload(data?.payload ?? null);
        // Populate decisions from snapshot so live decisions state isn't stale.
        if (Array.isArray(data?.payload?.decisions)) {
          setDecisions(data.payload.decisions);
          setDecisionsError(null);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        log.error("WeeklyPortfolioReview: snapshot load failed", {
          err:      String(err),
          weekDate: selectedWeek,
        });
        setSnapshotError(err?.message ?? "Failed to load archived snapshot.");
      })
      .finally(() => { if (!cancelled) setSnapshotLoading(false); });

    return () => { cancelled = true; };
  }, [selectedWeek, isFrozen, featureFlagsReady, projectsEnabled, officesEnabled]);

  // ── Derived display data ───────────────────────────────────────────────────
  //
  // WPR-SNAPSHOT: when frozen, all display state is derived from the snapshot
  // payload rather than from live query state (projects, locations, updates).
  // This is the core invariant of the snapshot architecture.

  // entity_id → update_text
  const updatesMap = useMemo(() => {
    if (isFrozen) {
      // Strictly snapshot-only: return empty Map until snapshot loads.
      // Never fall back to live updates data for a frozen week.
      if (!snapshotPayload) return new Map();
      const obj = snapshotPayload.updatesMap ?? {};
      return new Map(Object.entries(obj));
    }
    const m = new Map();
    updates.forEach((u) => {
      if (u.entityId) m.set(u.entityId, u.updateText ?? "");
    });
    return m;
  }, [isFrozen, snapshotPayload, updates]);

  // entity_id → { updatedBy, updatedAt } for provenance display
  // Only meaningful for live weeks (snapshot doesn't store per-update metadata).
  const updatesMetaMap = useMemo(() => {
    if (isFrozen) return new Map();
    const m = new Map();
    updates.forEach((u) => {
      if (u.entityId) m.set(u.entityId, { updatedBy: u.updatedBy ?? null, updatedAt: u.updatedAt ?? null });
    });
    return m;
  }, [isFrozen, updates]);

  // Build merged + sorted roll-up rows.
  // Frozen: strictly snapshot-only (never live fallback).
  //   - While snapshot loads → empty array (spinner shown by snapshotLoading flag).
  //   - Once snapshot arrives → use snapshotPayload.rows.
  // Live: compute from live data using assembly function.
  const rows = useMemo(() => {
    if (isFrozen) {
      if (!snapshotPayload) return [];
      return filterWprRowsByEligibility(snapshotPayload.rows, {
        projectsEnabled, officesEnabled, marketsEnabled,
      });
    }
    return filterWprRowsByEligibility(
      buildWprRows(projects, locations, milestones, updatesMap),
      { projectsEnabled, officesEnabled, marketsEnabled, locations },
    );
  }, [isFrozen, snapshotPayload, projects, locations, milestones, updatesMap, projectsEnabled, officesEnabled, marketsEnabled]);

  // Precomputed per-project display props for the read-out section.
  // Frozen: strictly snapshot-only (never live fallback).
  // Live:   compute from live data.
  const readOutProps = useMemo(() => {
    if (isFrozen) {
      if (!snapshotPayload) return [];
      return projectsEnabled && Array.isArray(snapshotPayload.readOuts) ? snapshotPayload.readOuts : [];
    }
    return projectsEnabled ? buildWprReadOutProps(projects, milestones) : [];
  }, [isFrozen, snapshotPayload, projects, milestones, projectsEnabled]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleSave = useCallback((entityType, entityId) => async (text) => {
    const updated = await upsertWprUpdate(entityType, entityId, text, selectedWeek);
    setUpdates((prev) => {
      const next = prev.filter((u) => u.entityId !== entityId);
      return [...next, updated];
    });
    // Ensure selectedWeek appears in the weeks list after first save.
    setWeeks((prev) => {
      if (prev.some((w) => w.weekDate === selectedWeek)) return prev;
      return [{ weekDate: selectedWeek, frozen: false }, ...prev];
    });
  }, [selectedWeek]);

  const weekLabel    = useMemo(() => weekDateLabel(selectedWeek), [selectedWeek]);

  // Open a new window with a print-ready version of the roll-up table.
  // Uses the browser's native PDF-save flow (Ctrl+P / Cmd+P → Save as PDF).
  // WPR-SNAPSHOT: no special casing needed — `rows` and `updatesMap` are
  // already snapshot-derived when isFrozen is true. Frozen PDF = snapshot data.
  const handleExportPdf = useCallback(() => {
    const html = buildWprPrintHtml(rows, updatesMap, weekLabel);
    if (!openPrintWindow(html)) {
      log.warn("WeeklyPortfolioReview: export popup blocked - check browser pop-up settings");
    }
  }, [rows, updatesMap, weekLabel]);
  const projectCount = rows.filter((r) => r.entityType === "project").length;
  const siteCount    = rows.filter((r) => r.entityType === "location").length;

  const loading = baseLoading || updatesLoading || snapshotLoading;

  if (!featureFlagsReady) {
    return (
      <ReportShell onBack={onBack}>
        <div data-testid="wpr-access-loading" style={{ padding: 32, color: T.mid }}>Checking access to Weekly Portfolio Review…</div>
      </ReportShell>
    );
  }

  if (!projectsEnabled && !officesEnabled) {
    return (
      <ReportShell onBack={onBack}>
        <div data-testid="wpr-unavailable" style={{ padding: 32, color: T.mid }}>
          Weekly Portfolio Review is unavailable because Projects and Offices are disabled.
        </div>
      </ReportShell>
    );
  }

  if (baseLoading) {
    return (
      <ReportShell onBack={onBack}>
        <div style={{ padding: 40, color: T.mid, fontSize: T.fsMd }}>
          Loading Weekly Portfolio Review…
        </div>
      </ReportShell>
    );
  }

  return (
    <ReportShell
      onBack={onBack}
      prevReport={prevReport}
      nextReport={nextReport}
      onNavigateReport={onNavigateReport}
      contentStyle={{ maxWidth: 1200, margin: "0 auto", padding: "28px 0 40px", width: "100%" }}
    >

      <ErrorBanner error={error} onDismiss={() => setError(null)} />

      {/* Week selector + action buttons */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <WeekPicker
            weeks={weeks}
            selectedWeek={selectedWeek}
            onSelect={(wk) => { setSelectedWeek(wk); }}
            disabled={loading}
          />

          {/* Action buttons */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            {/* Status badge — visible to all authenticated users; replaces the manual freeze button */}
            <span
              data-testid="wpr-status-badge"
              style={{
                fontSize: T.fsXs,
                fontWeight: T.fwSemibold,
                padding:      "3px 10px",
                borderRadius: 4,
                background:   isFrozen ? T.border : T.lightGray,
                color:        T.mid,
                border:       `1px solid ${T.border}`,
                whiteSpace:   "nowrap",
              }}
            >
              {isFrozen
                ? (() => {
                    const by    = selectedWeekInfo.frozenBy === "system" ? "auto" : selectedWeekInfo.frozenBy;
                    const atRaw = selectedWeekInfo.frozenAt;
                    const atFmt = atRaw
                      ? new Date(atRaw).toLocaleDateString("en-US", {
                          month: "short", day: "numeric", year: "numeric",
                        })
                      : null;
                    if (atFmt && by) return `Archived ${atFmt} by ${by}`;
                    if (atFmt)       return `Archived ${atFmt}`;
                    if (by)          return `Archived by ${by}`;
                    return "Archived";
                  })()
                : selectedWeek === currentMondayUtc() ? "Current week - unfrozen and live" : "Read-only - no archive snapshot"
              }
            </span>


            {rows.length > 0 && (
              <button
                type="button"
                data-testid="wpr-export-pdf"
                onClick={handleExportPdf}
                style={{
                  fontSize: T.fsBase,
                  fontWeight: T.fwSemibold,
                  padding:      "6px 14px",
                  borderRadius: 6,
                  background:   T.blue,
                  color:        T.white,
                  border:       "none",
                  cursor:       "pointer",
                  flexShrink:   0,
                }}
              >
                Export PDF
              </button>
            )}
          </div>
        </div>

        {snapshotError && (
          <p data-testid="wpr-snapshot-error" style={{ margin: "6px 0 0", fontSize: T.fsSm, color: T.red }}>
            {snapshotError}
          </p>
        )}

        {/* Archived metadata: shown when the selected week is frozen */}
        {isFrozen && (
          <p style={{ margin: "4px 0 0", fontSize: T.fsXs, color: T.mid }}>
            {(() => {
              const atRaw = selectedWeekInfo.frozenAt;
              const by    = selectedWeekInfo.frozenBy;
              const atFmt = atRaw
                ? new Date(atRaw).toLocaleDateString("en-US", {
                    month: "short", day: "numeric", year: "numeric",
                  })
                : null;
              if (atFmt && by)  return `Archived by ${by} on ${atFmt}`;
              if (atFmt)        return `Archived on ${atFmt}`;
              if (by)           return `Archived by ${by}`;
              return "Archived";
            })()}
          </p>
        )}

        <p style={{ margin: "6px 0 0", fontSize: T.fsBase, color: T.mid }}>
          {(updatesLoading || snapshotLoading) ? "Loading…" : (
            <>
              {rows.length} items - {" "}
              {projectCount} active {projectCount === 1 ? "project" : "projects"},{" "}
              {siteCount} flagged {siteCount === 1 ? "site" : "sites"}
            </>
          )}
        </p>
      </div>

      {/* Roll-up table */}
      <div style={{
        background:   T.card,
        borderRadius: 10,
        border:       `1px solid ${T.border}`,
        overflowX:    "auto",
        opacity:      (updatesLoading || snapshotLoading) ? 0.6 : 1,
        transition:   "opacity 0.15s",
      }}>
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: 36 }} />
            <col style={{ width: 76 }} />
            <col style={{ width: "22%" }} />
            <col />
            <col style={{ width: 190 }} />
            <col style={{ width: 140 }} />
          </colgroup>
          <thead>
            <tr style={{ borderBottom: `1px solid ${T.border}`, background: T.lightGray }}>
              <th style={TH_STYLE} />
              <th style={TH_STYLE}>Type</th>
              <th style={TH_STYLE}>Project / Site</th>
              <th style={TH_STYLE}>Update</th>
              <th style={TH_STYLE}>Next Milestone</th>
              <th style={TH_STYLE}>Owner</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  style={{
                    padding:   "32px 16px",
                    textAlign: "center",
                    color:     T.mid,
                    fontSize: T.fsBase,
                  }}
                >
                  No active projects or flagged sites this week.
                </td>
              </tr>
            )}
            {rows.map((row, i) => {
              const updateText = updatesMap.get(row.entityId) ?? "";
              const updateMeta = updatesMetaMap.get(row.entityId) ?? {};
              const isLast     = i === rows.length - 1;

              return (
                <tr
                  key={row.entityId}
                  style={{ borderBottom: isLast ? "none" : `1px solid ${T.border}` }}
                >
                  {/* RAG dot */}
                  <td style={{ ...TD_STYLE, textAlign: "center", paddingTop: 14 }}>
                    <RagDot color={row.ragColor} />
                  </td>

                  {/* Type pill */}
                  <td style={TD_STYLE}>
                    <TypePill entityType={row.entityType} />
                  </td>

                  {/* Name + subtext */}
                  <td style={TD_STYLE}>
                    <div style={{ fontWeight: T.fwSemibold, fontSize: T.fsBase, color: T.dark, lineHeight: 1.3 }}>
                      {row.name}
                    </div>
                    <div style={{ fontSize: T.fsXs, color: T.mid, marginTop: 2 }}>
                      {row.subtext}
                    </div>
                  </td>

                  {/* Editorial update */}
                  <td style={{ ...TD_STYLE, fontSize: T.fsSm }}>
                    <EditableUpdate
                      value={updateText}
                      canEdit={canEdit}
                      onSave={handleSave(row.entityType, row.entityId)}
                      updatedBy={updateMeta.updatedBy}
                      updatedAt={updateMeta.updatedAt}
                    />
                  </td>

                  {/* Next milestone */}
                  <td style={{ ...TD_STYLE, fontSize: T.fsSm, color: T.mid }}>
                    {row.milestone}
                  </td>

                  {/* Owner */}
                  <td style={{ ...TD_STYLE, fontSize: T.fsSm, color: T.mid }}>
                    {row.owner}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {canEdit && rows.length > 0 && (
        <p style={{ margin: "10px 0 0", fontSize: T.fsXs, color: T.mid }}>
          Click any Update cell to add or edit the editorial note for that item.
        </p>
      )}

      {/* ── Project Read-outs section ── */}
      {readOutProps.length > 0 && (
        <div style={{ marginTop: 36 }}>
          <PaneHeading style={{ marginBottom: 12 }}>Project read-outs</PaneHeading>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {readOutProps.map((proj) => (
              <WprProjectReadOut
                key={proj.projectId}
                project={proj}
                canEdit={canEdit}
                frozenTracks={isFrozen ? (proj.tracks ?? null) : null}
                frozenWeeklyUpdate={isFrozen ? (proj.weeklyUpdate ?? null) : null}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Decisions & Actions block ── */}
      {projectsEnabled && (decisions.length > 0 || decisionsError !== null) && (
        <DecisionsBlock
          decisions={decisions}
          decisionsError={decisionsError}
        />
      )}

    </ReportShell>
  );
}
